import type { RGB } from "./color-engine";

/**
 * Recolors a masked region of a photo while preserving its original
 * shadows, highlights, and texture — this is the "don't look like a flat
 * fake paint fill" requirement from the spec.
 *
 * Implemented as a pure per-pixel blend (the "color" blend mode from the
 * W3C Compositing spec: https://www.w3.org/TR/compositing-1/#blendingcolor)
 * rather than relying on canvas's native `globalCompositeOperation =
 * 'color'`. That native mode exists, but browser support for the
 * non-separable blend modes (hue/saturation/color/luminosity) has
 * historically been inconsistent between mobile Safari and desktop
 * Chrome/Firefox. Computing the blend manually guarantees pixel-identical
 * output on every device, which matters here specifically because the
 * app's requirement is that recoloring looks the same everywhere — not
 * "close enough" per-browser.
 */

// -----------------------------------------------------------------------
// Color blend mode (W3C Compositing spec, non-separable blend formulas)
// -----------------------------------------------------------------------

function luminosity({ r, g, b }: RGB): number {
  return 0.3 * r + 0.59 * g + 0.11 * b;
}

function clipColor(c: RGB): RGB {
  const lum = luminosity(c);
  const n = Math.min(c.r, c.g, c.b);
  const x = Math.max(c.r, c.g, c.b);

  let { r, g, b } = c;

  if (n < 0) {
    r = lum + ((r - lum) * lum) / (lum - n);
    g = lum + ((g - lum) * lum) / (lum - n);
    b = lum + ((b - lum) * lum) / (lum - n);
  }
  if (x > 255) {
    r = lum + ((r - lum) * (255 - lum)) / (x - lum);
    g = lum + ((g - lum) * (255 - lum)) / (x - lum);
    b = lum + ((b - lum) * (255 - lum)) / (x - lum);
  }

  return { r, g, b };
}

function setLum(c: RGB, l: number): RGB {
  const d = l - luminosity(c);
  return clipColor({ r: c.r + d, g: c.g + d, b: c.b + d });
}

/**
 * Blends `source` (the flat paint color) onto `backdrop` (the original
 * photo pixel), keeping the backdrop's luminosity — i.e. its shadow,
 * highlight, and texture detail — while taking hue/saturation from the
 * source color.
 */
export function blendColorMode(backdrop: RGB, source: RGB): RGB {
  return setLum(source, luminosity(backdrop));
}

// -----------------------------------------------------------------------
// Polygon mask (manual lasso selection — v1 per REVISED_SPEC.md 4c)
// -----------------------------------------------------------------------

export interface Point {
  x: number;
  y: number;
}

/**
 * Standard ray-casting point-in-polygon test. Pure and side-effect free
 * so it's unit-testable without a canvas.
 */
export function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersects =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;

    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Recolors every pixel inside `polygon` in place on the given ImageData,
 * using the luminosity-preserving blend above. Pixels outside the polygon
 * are left untouched.
 *
 * Runs a bounding-box pre-check before the exact point-in-polygon test on
 * each pixel — for a small lasso region on a large photo this avoids
 * running the (relatively expensive) polygon test over the whole image,
 * which matters for keeping this smooth on lower-powered mobile CPUs.
 */
export function recolorMaskedRegion(
  imageData: { data: Uint8ClampedArray; width: number; height: number },
  polygon: Point[],
  targetColor: RGB
): void {
  if (polygon.length < 3) return;

  const xs = polygon.map((p) => p.x);
  const ys = polygon.map((p) => p.y);
  const minX = Math.max(0, Math.floor(Math.min(...xs)));
  const maxX = Math.min(imageData.width - 1, Math.ceil(Math.max(...xs)));
  const minY = Math.max(0, Math.floor(Math.min(...ys)));
  const maxY = Math.min(imageData.height - 1, Math.ceil(Math.max(...ys)));

  for (let y = minY; y <= maxY; y++) {
    for (let x = minX; x <= maxX; x++) {
      if (!isPointInPolygon({ x, y }, polygon)) continue;

      const index = (y * imageData.width + x) * 4;
      const backdrop: RGB = {
        r: imageData.data[index],
        g: imageData.data[index + 1],
        b: imageData.data[index + 2],
      };
      const blended = blendColorMode(backdrop, targetColor);

      imageData.data[index] = clampByte(blended.r);
      imageData.data[index + 1] = clampByte(blended.g);
      imageData.data[index + 2] = clampByte(blended.b);
      // alpha channel (index + 3) left untouched
    }
  }
}

function clampByte(v: number): number {
  return Math.round(Math.min(255, Math.max(0, v)));
}
